import { Button, TextField } from "@mui/material";
import { useTranslation } from "react-i18next";
import "./PlaceSearchWithSavedLocations/SaveFavoriteLocationDialog.scss";
import { useAppDispatch } from "../redux-store";
import { createCustomerDeliveryLocation } from "../ducks/data/economics/customer-delivery-locations";
import { AutoSuggestItem } from "dora-shared";
import { FormProvider, useForm } from "react-hook-form";
import { useEffect, useState } from "react";
import NewPlaceSearch, { AddressChange } from "./NewPlaceSearch";

type FormValues = {
  name: string;
  address: string;
  city: string;
  postcode: string;
  country: string;
};

const DeliveryLocationForm = ({
  clientId,
  prefill,
  onSaved,
  onCancel,
}: {
  clientId: string;
  /**
   * The place-search result to prefill from. When omitted the form shows its
   * own place search, since coordinates are required either way before saving.
   */
  prefill?: AutoSuggestItem;
  onSaved: () => void;
  onCancel: () => void;
}) => {
  const { t } = useTranslation(["modals", "components", "shipmentModal"]);
  const dispatch = useAppDispatch();
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Populated once the selected place has been looked up; until then we don't
  // have coordinates / address parts, so saving is disabled.
  const [coord, setCoord] = useState<{ lon: number; lat: number } | null>(null);
  const [googlePlaceId, setGooglePlaceId] = useState<string | undefined>();

  const formMethods = useForm<FormValues>({
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {
      name: "",
      address: "",
      city: "",
      postcode: "",
      country: "",
    },
  });

  const { register, setValue, handleSubmit } = formMethods;

  useEffect(() => {
    if (!prefill) {
      return;
    }
    prefill.lookup().then((lookupResult) => {
      // Prefill the name with the address; the user will typically replace it.
      setValue("name", lookupResult.address || "");
      setValue("address", lookupResult.address || "");
      setValue("city", lookupResult.city || "");
      setValue("postcode", lookupResult.postcode || "");
      setValue("country", lookupResult.countryCode || "");
      setCoord({
        lon: lookupResult.coords.lon,
        lat: lookupResult.coords.lat,
      });
      setGooglePlaceId(lookupResult.googlePlaceId);
    });
  }, [setValue, prefill]);

  const onPlaceSelected = (addressChange: AddressChange | null) => {
    if (!addressChange) {
      setCoord(null);
      setGooglePlaceId(undefined);
      return;
    }
    setValue("name", addressChange.address || "");
    setValue("address", addressChange.address || "");
    setValue("city", addressChange.city || "");
    setValue("postcode", addressChange.postcode || "");
    setValue("country", addressChange.country || "");
    setCoord({
      lon: addressChange.coord.lon,
      lat: addressChange.coord.lat,
    });
    setGooglePlaceId(addressChange.googlePlaceId);
  };

  const onSaveClick = () => {
    handleSubmit(async (values) => {
      setIsSubmitting(true);
      try {
        await dispatch(
          createCustomerDeliveryLocation({
            clientId,
            name: values.name,
            address: values.address || null,
            postalCode: values.postcode || null,
            city: values.city || null,
            country: values.country || null,
            ...(coord && {
              resolvedPlace: {
                address: values.address || null,
                postcode: values.postcode || null,
                city: values.city || null,
                country: values.country || null,
                placeName: null,
                coord,
                ...(googlePlaceId && { googlePlaceId }),
              },
            }),
          })
        ).unwrap();
        onSaved();
      } catch {
        // The createCustomerDeliveryLocation thunk has already reported it and shown the error toast.
      } finally {
        setIsSubmitting(false);
      }
    })();
  };

  return (
    <FormProvider {...formMethods}>
      <div className="save-favorite-location__rest-of-form">
        {!prefill && (
          <div className="add-edit-favorites-terminals__form-row">
            <NewPlaceSearch onPlaceSelected={onPlaceSelected} placeError={false} />
          </div>
        )}
        <div className="add-edit-favorites-terminals__form-row">
          <TextField
            InputLabelProps={{ shrink: true }}
            label={t("modals:saveDeliveryAddress.nameLabel")}
            {...register("name")}
          ></TextField>
        </div>
        <div className="add-edit-favorites-terminals__form-row">
          <TextField
            InputLabelProps={{ shrink: true }}
            label={t("shipmentModal:inputs.searchPlaceAddressPlaceholder")}
            {...register("address")}
          ></TextField>
        </div>
        <div className="add-edit-favorites-terminals__form-row">
          <TextField
            InputLabelProps={{ shrink: true }}
            label={t("shipmentModal:inputs.searchPlaceCityPlaceholder")}
            className={"add-edit-favorites-terminals__city-input"}
            {...register("city")}
          ></TextField>
          <TextField
            InputLabelProps={{ shrink: true }}
            label={t("shipmentModal:inputs.searchPlaceZipPlaceholder")}
            className={"add-edit-favorites-terminals__postal-code-input"}
            {...register("postcode")}
          ></TextField>
        </div>
        <div className="add-edit-favorites-terminals__form-row">
          <TextField
            InputLabelProps={{ shrink: true }}
            label={t("shipmentModal:inputs.searchPlaceCountryPlaceholder")}
            {...register("country")}
          ></TextField>
        </div>
      </div>

      <hr className="save-favorite-location__hr" />
      <div className="save-favorite-location__buttons">
        <Button variant="outlined" color="primary" onClick={onCancel}>
          {t("components:buttonLabels.cancel")}
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={!coord || isSubmitting}
          onClick={onSaveClick}
        >
          {t("components:buttonLabels.save")}
        </Button>
      </div>
    </FormProvider>
  );
};

export default DeliveryLocationForm;
